import { LicenseInfo } from '@mui/x-license';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { hydrateStartThunk } from '@redux/hydration/hydrationThunks';
import AlertInfoGlobal from 'components/AlertInfoGlobal/AlertInfoGlobal';
import ClientOnly from 'components/ClientOnly/ClientOnly';
import Config from 'config/config';
import { useSlotConfig } from 'utils/useSlotConfig';
import { routesForAuthenticatedOnly, routesForAuthenticatedOnlyNewDashboard, routesForNotAuthenticatedOnly, routesForPublic } from 'navigation/routes';
import ApiClient from './ApiClient';
import { getAuthPersonIdSelector } from './redux/oauth/oauthSelectors';
import { getPersonByIdSelector } from './redux/person/personSelector';

LicenseInfo.setLicenseKey(Config.getMuiProKey());

const FULLSLEEP_ORG_ID = '00000000-0000-0000-0000-000000000001';

const isNewDashboardUser = (orgId: Nullable<string>): boolean => !!orgId && orgId !== FULLSLEEP_ORG_ID;

export default function App() {
  const flags = useFlags();
  const authPersonId = useSelector(getAuthPersonIdSelector);
  const personById = useSelector(getPersonByIdSelector);
  const authOrgId = personById && authPersonId ? personById[authPersonId]?.organizationId : undefined;
  const isNewDashboard = isNewDashboardUser(authOrgId);

  const routes = [...routesForPublic, ...routesForNotAuthenticatedOnly, ...(isNewDashboard ? routesForAuthenticatedOnlyNewDashboard : routesForAuthenticatedOnly)];
  const router = createBrowserRouter(routes);

  const [initDone, setInitDone] = useState(false);
  const dispatch = useDispatch();

  // Enable slot-based routing based on LaunchDarkly flag
  useSlotConfig();

  useEffect(() => {
    if (Config.getCognitoEnabled()) {
      ApiClient.getInstance()
        .refreshAccessToken()
        .catch((error: Error) => {
          console.error('error fetching access token:', error);
        })
        .finally(() => {
          dispatch(hydrateStartThunk());
          setInitDone(true);
        });
    } else {
      setInitDone(true);
    }
  }, []);

  if (!initDone) {
    return null;
  }

  return (
    <ClientOnly>
      <AlertInfoGlobal />
      <RouterProvider router={router} />
    </ClientOnly>
  );
}
